import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { Game, Standard } from '../../../data/types';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import StudentLayout from '../../layouts/StudentLayout';
import { AuthContext } from '../../../App';

const GameGrid: React.FC = () => {
  const { standardId } = useParams<{ standardId: string }>();
  const navigate = useNavigate();
  const { activeRole } = React.useContext(AuthContext);
  
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [currentStandard, setCurrentStandard] = useState<Standard | null>(null);
  const [loading, setLoading] = useState(true);
  const [difficultyFilter, setDifficultyFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  
  // Set student session flag when component mounts
  useEffect(() => {
    // Always set the student session flag to true when accessing this page
    localStorage.setItem('isStudentSession', 'true');
    
    // Only redirect if explicitly authenticated as a non-student role
    if (activeRole && activeRole !== 'Student') {
      navigate('/student/login');
      return;
    }
  }, [navigate, activeRole]);
  
  // Ensure the student session flag is set
  useEffect(() => {
    if (!localStorage.getItem('isStudentSession')) {
      localStorage.setItem('isStudentSession', 'true');
    }
  }, []);
  
  // Get unique game types from the games array using a more compatible approach
  const gameTypes = games
    .map(game => game.type)
    .filter((type, index, self) => self.indexOf(type) === index);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch games and standard data
        const allGames = await dataService.getGames();
        const standards = await dataService.getStandards();
        
        setGames(allGames);
        
        // Find current standard
        if (standardId) {
          const standard = standards.find(s => s.id === standardId);
          setCurrentStandard(standard || null);
          
          // Filter games by standard
          const standardGames = allGames.filter(game => 
            game.supportedStandards.includes(standardId)
          );
          setFilteredGames(standardGames);
        } else {
          // If no standard is specified, show all games
          setFilteredGames(allGames);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching game data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [standardId]);
  
  useEffect(() => {
    // Apply filters when they change
    let result = [...games];
    
    // Filter by standard if one is selected
    if (standardId) {
      result = result.filter(game => game.supportedStandards.includes(standardId));
    }
    
    // Apply difficulty filter if selected
    if (difficultyFilter) {
      result = result.filter(game => game.difficulty === difficultyFilter);
    }
    
    // Apply type filter if selected
    if (typeFilter) {
      result = result.filter(game => game.type === typeFilter);
    }
    
    setFilteredGames(result);
  }, [games, standardId, difficultyFilter, typeFilter]);
  
  const handleGameClick = (gameId: string) => {
    // In a real app, this would navigate to the game play screen
    navigate(`/student/game/${gameId}`);
  };
  
  const handleBackClick = () => {
    navigate(-1);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-b from-blue-50 to-purple-50">
        <div className="animate-bounce text-4xl text-purple-600">
          Loading awesome games...
        </div>
      </div>
    );
  }
  
  return (
    <StudentLayout>
      <div className="p-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500">
              Math Games
            </h1>
            {currentStandard && (
              <div className="mt-2">
                <Badge variant="primary">{currentStandard.code}</Badge>
                <span className="ml-2 text-gray-600">{currentStandard.description}</span>
              </div>
            )}
          </div>
          <Button 
            variant="secondary" 
            onClick={handleBackClick}
            className="mt-4 md:mt-0 rounded-lg"
          >
            <span className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              Back
            </span>
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
            <div className="flex gap-2">
                <Button 
                  variant={difficultyFilter === 'Easy' ? 'primary' : 'secondary'} 
                  onClick={() => setDifficultyFilter(difficultyFilter === 'Easy' ? null : 'Easy')}
                  className="rounded-lg flex-1"
                >
                  Easy
                </Button>
                <Button 
                  variant={difficultyFilter === 'Medium' ? 'primary' : 'secondary'} 
                  onClick={() => setDifficultyFilter(difficultyFilter === 'Medium' ? null : 'Medium')}
                  className="rounded-lg flex-1"
                >
                  Medium
                </Button>
                <Button 
                  variant={difficultyFilter === 'Hard' ? 'primary' : 'secondary'} 
                  onClick={() => setDifficultyFilter(difficultyFilter === 'Hard' ? null : 'Hard')}
                  className="rounded-lg flex-1"
                >
                  Hard
                </Button>
            </div>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Game Type</label>
            <div className="flex flex-wrap gap-2">
              {gameTypes.map(type => (
                <Button 
                  key={type}
                  variant={typeFilter === type ? 'primary' : 'secondary'} 
                  onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                  className="rounded-lg"
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Game Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {filteredGames.length > 0 ? (
          filteredGames.map(game => (
            <div 
              key={game.id} 
              className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all overflow-hidden cursor-pointer transform hover:-translate-y-1"
              onClick={() => handleGameClick(game.id)}
            >
              <div className="relative h-40 bg-gray-200">
                <img 
                  src={game.thumbnail} 
                  alt={game.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2">
                  <Badge variant={
                    game.difficulty === 'Easy' ? 'success' : 
                    game.difficulty === 'Medium' ? 'warning' : 'error'
                  }>
                    {game.difficulty}
                  </Badge>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <h3 className="font-bold text-white text-lg">{game.title}</h3>
                </div>
              </div>
              <div className="p-3">
                <div className="flex justify-between items-center">
                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                    {game.type}
                  </span>
                  <span className="text-xs text-gray-500">Grade {game.gradeLevel}</span>
                </div>
                <div className="mt-3">
                  <Button variant="primary" className="w-full rounded-lg">
                    <span className="flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      </svg>
                      Play Now
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-xl shadow-md p-8 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Games Found</h3>
            <p className="text-gray-500 mb-4">
              We couldn't find any games matching your current filters.
            </p>
            <Button 
              variant="primary" 
              onClick={() => {
                setDifficultyFilter(null);
                setTypeFilter(null);
              }}
              className="rounded-lg"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
      
      {/* Game Recommendations */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-purple-600 to-blue-500 p-4 text-white">
          <h2 className="text-xl font-bold">Recommended for You</h2>
          <p className="text-sm opacity-90">Games that match your learning style</p>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {games.slice(0, 3).map(game => (
            <div 
              key={`rec-${game.id}`} 
              className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => handleGameClick(game.id)}
            >
              <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 mr-3">
                <img 
                  src={game.thumbnail} 
                  alt={game.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{game.title}</h3>
                <div className="flex items-center mt-1">
                  <Badge variant={
                    game.difficulty === 'Easy' ? 'success' : 
                    game.difficulty === 'Medium' ? 'warning' : 'error'
                  } className="mr-2">
                    {game.difficulty}
                  </Badge>
                  <span className="text-xs text-gray-500">{game.type}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Achievement Opportunity */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 flex items-center">
          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="flex-grow">
            <h3 className="font-bold text-gray-800">Game Master Achievement</h3>
            <p className="text-sm text-gray-600">Play 5 different games to earn this special achievement!</p>
          </div>
          <div className="ml-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-500">2/5</div>
              <div className="text-xs text-gray-500">games played</div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </StudentLayout>
  );
};

export default GameGrid;
